import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../contexts/AuthContext";
import {
  getActiveMemberships,
  getCallPreps,
  getCallPrepsLast7Days,
  getCallPrepsThisWeek,
  getCallPrepsThisMonth,
  getUpcomingGMOrderExpirations,
  getAuditsThisMonth,
  getOverdueTasks,
  getAccountsWithNoOpenActivities,
  getOverdueInvoices,
  getAccountsByProduct,
  getTasksCompletedThisWeek,
  getTasksCompletedThisMonth,
  getCompletedDirectorAccountReviews,
  getPausedProducts,
  getCompletionOfSAItasks,
  getOverdueDARs,
  // DataTable-specific functions
  getCallPrepsForTable,
  getCallPrepsThisWeekForTable,
  getCallPrepsThisMonthForTable,
  getUpcomingGMOrderExpirationsForTable,
  getCompletedDirectorAccountReviewsForTable,
  getOverdueInvoicesForTable,
  getPausedProductsForTable,
  getCompletionOfSAItasksForTable,
  getAccountsByProductForTable,
  getActiveMembershipsForTable,
  getAuditsThisMonthForTable,
  getAccountsWithInactiveAE,
  getConcernedAccounts,
  getHighRiskAccounts,
  getCancellingAccounts,
  getOverdueDARsForTable,
} from "./coordinatorsQueries";

const DataTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { type, color } = useParams();
  const context = useAuthContext();
  const navigate = useNavigate();

  // Function to determine the status color and reason
  const getStatusInfo = (item, dataType) => {
    const today = new Date();

    switch (dataType) {
      case "callPreps":
        const daysDiff = item.DaysBetween || 0;
        if (daysDiff <= 1) return { color: "green", reason: "On Time" };
        if (daysDiff <= 3)
          return { color: "yellow", reason: `${daysDiff} days overdue` };
        return { color: "red", reason: `${daysDiff} days overdue` };

      case "completedDirectorAccountReviews":
        const reviewDays = item.DaysBetween || 0;
        if (reviewDays <= 1)
          return { color: "green", reason: "Completed on time" };
        if (reviewDays <= 2)
          return { color: "yellow", reason: `${reviewDays} days late` };
        return { color: "red", reason: `${reviewDays} days late` };

      case "overdueInvoices":
        const dueDate = new Date(item.DueDate);
        const overdueDays = Math.floor(
          (today - dueDate) / (1000 * 60 * 60 * 24)
        );
        if (overdueDays <= 0) return { color: "green", reason: "Current" };
        if (overdueDays <= 5)
          return { color: "yellow", reason: `${overdueDays} days overdue` };
        return { color: "red", reason: `${overdueDays} days overdue` };

      case "pausedProducts":
        const expirationDate = new Date(item.ExpirationDate);
        const daysUntilExpiration = Math.floor(
          (expirationDate - today) / (1000 * 60 * 60 * 24)
        );
        if (daysUntilExpiration > 30)
          return { color: "green", reason: "Sufficient time" };
        if (daysUntilExpiration > 0)
          return {
            color: "yellow",
            reason: `Expires in ${daysUntilExpiration} days`,
          };
        return { color: "red", reason: "Expired" };

      case "completionOfSAItasks":
        const saiDaysDiff = item.DaysBetween || 0;
        if (saiDaysDiff <= 1) return { color: "green", reason: "On Time" };
        if (saiDaysDiff <= 3)
          return { color: "yellow", reason: `${saiDaysDiff} days overdue` };
        return { color: "red", reason: `${saiDaysDiff} days overdue` };

      case "overdueDARs":
        const darDaysDiff = item.daysSinceLastDAR || 0;
        if (darDaysDiff <= 90)
          return { color: "green", reason: "On Time (≤90 days)" };
        if (darDaysDiff <= 120)
          return {
            color: "yellow",
            reason: `${darDaysDiff} days (91-120 days)`,
          };
        return { color: "red", reason: `${darDaysDiff} days (>120 days)` };

      case "overdueTasks":
        return { color: "red", reason: "Task is overdue" };

      case "noActivities":
        return { color: "red", reason: "No planned activities" };

      case "upcomingGMOrderExpirations":
        const daysUntilGMExpiration = item.DaysUntilExpiration || 0;
        if (daysUntilGMExpiration >= 20)
          return { color: "green", reason: "Sufficient time" };
        if (daysUntilGMExpiration >= 10)
          return {
            color: "yellow",
            reason: `Expires in ${daysUntilGMExpiration} days`,
          };
        return {
          color: "red",
          reason: `Expires in ${daysUntilGMExpiration} days`,
        };

      case "accountsWithInactiveAE":
        return { color: "red", reason: "Inactive Account Executive" };

      case "concernedAccounts":
        const concernLevel = item.ConcernLevel || 0;
        if (concernLevel >= 5)
          return { color: "red", reason: "High concern level" };
        if (concernLevel >= 1)
          return { color: "yellow", reason: "Moderate concern level" };
        return { color: "green", reason: "Low concern level" };

      case "highRiskAccounts":
        return { color: "red", reason: "High risk account" };

      case "cancellingAccounts":
        return { color: "red", reason: "Account cancelling" };

      default:
        return { color: "gray", reason: "Unknown" };
    }
  };

  // Function to open Salesforce record
  const openSalesforceRecord = (recordId, objectType = "Account") => {
    if (recordId) {
      const salesforceUrl = `https://gargleinc.lightning.force.com/lightning/r/${objectType}/${recordId}/view`;
      window.open(salesforceUrl, "_blank");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        let result;
        let cols;

        switch (type) {
          case "activeMemberships":
            result = await getActiveMembershipsForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Status", accessor: "Status" },
              { header: "Activated Date", accessor: "ActivatedDate" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "callPreps":
          case "callPrepsThisWeek":
          case "callPrepsThisMonth":
            result = await getCallPrepsForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Task Subject", accessor: "Subject" },
              { header: "Due Date", accessor: "DueDate" },
              { header: "Days Between", accessor: "DaysBetween" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "completedDirectorAccountReviews":
            result = await getCompletedDirectorAccountReviewsForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Review Date", accessor: "ReviewDate" },
              { header: "Days Between", accessor: "DaysBetween" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "auditsThisMonth":
          case "audits":
            result = await getAuditsThisMonthForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Audit Date", accessor: "AuditDate" },
              { header: "Status", accessor: "Status" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "upcomingGMOrderExpirations":
            result = await getUpcomingGMOrderExpirationsForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Order Label", accessor: "OrderLabel" },
              { header: "Expiration Date", accessor: "ExpirationDate" },
              {
                header: "Days Until Expiration",
                accessor: "DaysUntilExpiration",
              },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "overdueTasks":
            const overdueTasksResponse = await getOverdueTasks(context);
            result = overdueTasksResponse.data || overdueTasksResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Task Subject", accessor: "Subject" },
              { header: "Due Date", accessor: "ActivityDate" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "noOpenActivities":
            const noActivitiesResponse = await getAccountsWithNoOpenActivities(
              context
            );
            result = noActivitiesResponse.data || noActivitiesResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Last Activity Date", accessor: "LastActivityDate" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "overdueInvoices":
            result = await getOverdueInvoicesForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Invoice Number", accessor: "InvoiceNumber" },
              { header: "Due Date", accessor: "DueDate" },
              { header: "Amount", accessor: "Amount" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "accountsByProduct":
            result = await getAccountsByProductForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Product", accessor: "Product" },
              { header: "Status", accessor: "Status" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "pausedProducts":
            result = await getPausedProductsForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Product", accessor: "Product" },
              { header: "Expiration Date", accessor: "ExpirationDate" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "completionOfSAItasks":
            result = await getCompletionOfSAItasksForTable(context);
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "SAI Task", accessor: "Subject" },
              { header: "Completion Date", accessor: "CompletionDate" },
              { header: "Days Between", accessor: "DaysBetween" },
              { header: "Status Reason", accessor: "reason" },
            ];
            break;

          case "overdueDARs":
            result = await getOverdueDARsForTable(context);
            cols = [
              { header: "Coordinator", accessor: "Gargle_Coordinator__r.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Last DAR Date", accessor: "lastDARDate" },
              { header: "Days Since Last DAR", accessor: "daysSinceLastDAR" },
              { header: "Status", accessor: "status" },
            ];
            break;

          case "tasksCompletedThisWeek":
          case "tasksCompletedThisMonth":
            const tasksResponse =
              type === "tasksCompletedThisWeek"
                ? await getTasksCompletedThisWeek(context)
                : await getTasksCompletedThisMonth(context);
            result = tasksResponse.data || tasksResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "What.Name" },
              { header: "Task Subject", accessor: "Subject" },
              { header: "Completed Date", accessor: "ActivityDate" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "accountsWithInactiveAE":
            const inactiveAEResponse = await getAccountsWithInactiveAE(context);
            result = inactiveAEResponse.data || inactiveAEResponse;
            cols = [
              { header: "Account Executive", accessor: "AccountExecutive" },
              { header: "Account Name", accessor: "Name" },
              { header: "Status", accessor: "Status" },
              { header: "Last Activity Date", accessor: "LastActivityDate" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "concernedAccounts":
            const concernedResponse = await getConcernedAccounts(context);
            result = concernedResponse.data || concernedResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Concern Level", accessor: "ConcernLevel" },
              { header: "Last Updated", accessor: "LastUpdated" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "highRiskAccounts":
            const highRiskResponse = await getHighRiskAccounts(context);
            result = highRiskResponse.data || highRiskResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Risk Level", accessor: "RiskLevel" },
              { header: "Last Updated", accessor: "LastUpdated" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          case "cancellingAccounts":
            const cancellingResponse = await getCancellingAccounts(context);
            result = cancellingResponse.data || cancellingResponse;
            cols = [
              { header: "Owner", accessor: "Owner.Name" },
              { header: "Account Name", accessor: "Name" },
              { header: "Cancellation Date", accessor: "CancellationDate" },
              { header: "Reason Code", accessor: "ReasonCode" },
              { header: "Reason", accessor: "reason" },
            ];
            break;

          default:
            throw new Error("Invalid data type");
        }

        // Ensure result is always an array
        const resultArray = Array.isArray(result) ? result : [];
        console.log(`[DataTable] Type: ${type}, Result:`, resultArray);

        // Add status information to each record
        const enhancedResult = resultArray.map((item) => {
          const statusInfo = getStatusInfo(item, type);
          return {
            ...item,
            statusColor: statusInfo.color,
            reason: statusInfo.reason,
          };
        });

        setData(enhancedResult);
        setColumns(cols);

        // Filter by color if specified
        if (color && color !== "all") {
          const filtered = enhancedResult.filter(
            (item) => item.statusColor === color
          );
          setFilteredData(filtered);
        } else {
          setFilteredData(enhancedResult);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [type, color, context]);

  // Function to get nested object values
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Function to get row background color based on status
  const getRowColor = (item) => {
    switch (item.statusColor) {
      case "green":
        return "#d4edda";
      case "yellow":
        return "#fff3cd";
      case "red":
        return "#f8d7da";
      default:
        return "white";
    }
  };

  if (loading)
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>Loading data...</h3>
      </div>
    );

  if (error)
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
        <h3>Error loading data</h3>
        <button onClick={() => navigate(-1)} className="btn btn-secondary">
          Back to Dashboard
        </button>
      </div>
    );

  const displayTitle = () => {
    const typeNames = {
      callPreps: "Call Preps",
      callPrepsThisWeek: "Call Preps This Week",
      callPrepsThisMonth: "Call Preps This Month",
      completedDirectorAccountReviews: "Director Account Reviews",
      overdueInvoices: "Overdue Invoices",
      pausedProducts: "Paused Products",
      completionOfSAItasks: "SAI Task Completion",
      overdueTasks: "Overdue Tasks",
      noOpenActivities: "Accounts with No Activities",
      accountsByProduct: "Accounts by Product",
      activeMemberships: "Active Memberships",
      auditsThisMonth: "Audits This Month",
      audits: "Audits",
      upcomingGMOrderExpirations: "Upcoming GM Order Expirations",
      tasksCompletedThisWeek: "Tasks Completed This Week",
      tasksCompletedThisMonth: "Tasks Completed This Month",
      accountsWithInactiveAE: "Accounts with Inactive Account Executive",
      concernedAccounts: "Concerned Accounts",
      highRiskAccounts: "High Risk Accounts",
      cancellingAccounts: "Cancelling Accounts",
      overdueDARs: "Overdue DARs",
    };

    const colorNames = {
      green: "On Time",
      yellow: "Needs Attention",
      red: "Overdue/Urgent",
      all: "All Records",
    };

    return `${typeNames[type] || type} - ${colorNames[color] || color} (${
      filteredData.length
    } records)`;
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="btn btn-secondary"
          style={{ padding: "8px 16px" }}
        >
          ← Back to Dashboard
        </button>
        <h2 style={{ margin: 0, color: "#333" }}>{displayTitle()}</h2>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => navigate(`/coordinatorsDashboard/data/${type}/all`)}
            className={`btn ${
              color === "all" ? "btn-primary" : "btn-outline-secondary"
            }`}
            style={{ padding: "6px 12px", fontSize: "12px" }}
          >
            All
          </button>
          <button
            onClick={() =>
              navigate(`/coordinatorsDashboard/data/${type}/green`)
            }
            className={`btn ${
              color === "green" ? "btn-success" : "btn-outline-success"
            }`}
            style={{ padding: "6px 12px", fontSize: "12px" }}
          >
            On Time
          </button>
          <button
            onClick={() =>
              navigate(`/coordinatorsDashboard/data/${type}/yellow`)
            }
            className={`btn ${
              color === "yellow" ? "btn-warning" : "btn-outline-warning"
            }`}
            style={{ padding: "6px 12px", fontSize: "12px" }}
          >
            Attention
          </button>
          <button
            onClick={() => navigate(`/coordinatorsDashboard/data/${type}/red`)}
            className={`btn ${
              color === "red" ? "btn-danger" : "btn-outline-danger"
            }`}
            style={{ padding: "6px 12px", fontSize: "12px" }}
          >
            Urgent
          </button>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <h4>No records found for the selected criteria</h4>
        </div>
      ) : (
        <div
          className="table-responsive"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <table
            className="table table-striped table-hover"
            style={{
              backgroundColor: "white",
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            <thead
              style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}
            >
              <tr>
                {columns.map((col, index) => {
                  // Details columns (Subject, Task details, etc.) should be 30% width
                  const isDetailsColumn =
                    col.header.includes("Subject") ||
                    col.header.includes("Task") ||
                    col.header.includes("Details") ||
                    col.header.includes("SAI Task") ||
                    col.header.includes("Opportunity Name");

                  // Calculate width for other columns
                  const detailsColumns = columns.filter(
                    (c) =>
                      c.header.includes("Subject") ||
                      c.header.includes("Task") ||
                      c.header.includes("Details") ||
                      c.header.includes("SAI Task") ||
                      c.header.includes("Opportunity Name")
                  ).length;

                  const otherColumnsCount = columns.length - detailsColumns;
                  const otherColumnWidth =
                    otherColumnsCount > 0
                      ? `${70 / otherColumnsCount}%`
                      : "auto";

                  return (
                    <th
                      key={index}
                      style={{
                        padding: "12px",
                        borderBottom: "2px solid #dee2e6",
                        width: isDetailsColumn ? "30%" : otherColumnWidth,
                        wordWrap: "break-word",
                        overflow: "hidden",
                      }}
                    >
                      {col.header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr
                  key={index}
                  onClick={() =>
                    openSalesforceRecord(
                      row.Id || row.AccountId,
                      row.Id ? "Account" : "Task"
                    )
                  }
                  style={{
                    backgroundColor: getRowColor(row),
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.01)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {columns.map((col, colIndex) => {
                    const isDetailsColumn =
                      col.header.includes("Subject") ||
                      col.header.includes("Task") ||
                      col.header.includes("Details") ||
                      col.header.includes("SAI Task") ||
                      col.header.includes("Opportunity Name");

                    return (
                      <td
                        key={colIndex}
                        style={{
                          padding: "12px",
                          verticalAlign: "middle",
                          wordWrap: "break-word",
                          overflow: "hidden",
                          textOverflow: isDetailsColumn ? "ellipsis" : "clip",
                          maxWidth: isDetailsColumn ? "200px" : "none",
                        }}
                        title={
                          isDetailsColumn
                            ? getNestedValue(row, col.accessor)
                            : undefined
                        }
                      >
                        {getNestedValue(row, col.accessor) || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          marginTop: "20px",
          fontSize: "14px",
          color: "#666",
          textAlign: "center",
        }}
      >
        💡 Click on any row to open the record in Salesforce
      </div>
    </div>
  );
};

export default DataTable;
